import React from 'react';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useSettings } from '../context/SettingsContext';
import { Button } from '../components/ul/button';
import { Input } from '../components/ul/input';
import { Textarea } from '../components/ul/textarea';
import { toast } from '../hooks/use-toast';

const ContactPage = () => {
    const { settings, loading } = useSettings();

    const contactInfo = loading ? {
        address: '',
        city: 'Ankara',
        phone: '',
        phone2: '',
        email: '',
        workingHours: '',
        mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d22777.497515672836!2d32.74379843914518!3d40.01439127250976!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14d34b0c44551219%3A0x8c2fe7608e88cf58!2sAnkara%20Merkez%20Far!5e1!3m2!1str!2str!4v1730756153873!5m2!1str!2str'
    } : {
        address: settings.address || '',
        city: settings.city || 'Ankara',
        phone: settings.phone || '',
        phone2: settings.phone2 || '',
        email: settings.email || '',
        workingHours: settings.workingHours || '',
        mapEmbed: settings.mapEmbed || 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d22777.497515672836!2d32.74379843914518!3d40.01439127250976!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14d34b0c44551219%3A0x8c2fe7608e88cf58!2sAnkara%20Merkez%20Far!5e1!3m2!1str!2str!4v1730756153873!5m2!1str!2str'
    };

    const [formData, setFormData] = React.useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        toast({
            title: "Mesajınız gönderildi!",
            description: "En kısa sürede sizinle iletişime geçeceğiz.",
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
    };

    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    return (
        <div className="min-h-screen">
            <SEO
                title="İletişim | Ankara Merkez Far"
                description={`${contactInfo.address} - Ankara Merkez Far ile iletişime geçin. Far ve stop tamiri için bizi arayın: ${contactInfo.phone}`}
                keywords="iletişim ankara far, far tamirci iletişim, ankara far servisi telefon, adres far tamircisi"
                url="https://ankaramerkezfar.com/iletisim"
            />
            <Header />

            {/* Page Header */}
            <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16 md:py-20">
                <div className="container mx-auto px-4">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4">İletişim</h1>
                    <p className="text-lg text-gray-300 max-w-2xl">
                        Soru, öneri veya randevu için bizimle iletişime geçin.
                    </p>
                </div>
            </section>

            {/* Contact Content */}
            <section className="py-16 md:py-24 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="grid md:grid-cols-2 gap-12">
                        {/* Contact Info */}
                        <div className="space-y-8">
                            <div>
                                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                                    İletişim Bilgileri
                                </h2>
                                <p className="text-gray-600 mb-8">
                                    Her türlü soru ve talebiniz için bize ulaşabilirsiniz. Uzman ekibimiz size en kısa sürede dönüş yapacaktır.
                                </p>
                            </div>

                            <div className="space-y-6">
                                <div className="flex items-start space-x-4 bg-white p-6 rounded-lg shadow-md">
                                    <div className="bg-blue-100 p-3 rounded-lg">
                                        <MapPin className="text-blue-600" size={24} />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900 mb-1">Adres</h3>
                                        <p className="text-gray-600">
                                            {contactInfo.address}<br />
                                            {contactInfo.city}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-4 bg-white p-6 rounded-lg shadow-md">
                                    <div className="bg-blue-100 p-3 rounded-lg">
                                        <Phone className="text-blue-600" size={24} />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900 mb-1">Telefon</h3>
                                        <a href={`tel:${contactInfo.phone}`} className="text-gray-600 hover:text-blue-600 transition-colors">
                                            {contactInfo.phone}
                                        </a>
                                        <br />
                                        <a href={`tel:${contactInfo.phone2}`} className="text-gray-600 hover:text-blue-600 transition-colors">
                                            {contactInfo.phone2}
                                        </a>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-4 bg-white p-6 rounded-lg shadow-md">
                                    <div className="bg-blue-100 p-3 rounded-lg">
                                        <Mail className="text-blue-600" size={24} />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900 mb-1">E-posta</h3>
                                        <a href={`mailto:${contactInfo.email}`} className="text-gray-600 hover:text-blue-600 transition-colors">
                                            {contactInfo.email}
                                        </a>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-4 bg-white p-6 rounded-lg shadow-md">
                                    <div className="bg-blue-100 p-3 rounded-lg">
                                        <Clock className="text-blue-600" size={24} />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900 mb-1">Çalışma Saatleri</h3>
                                        <p className="text-gray-600">
                                            {contactInfo.workingHours || 'Pazartesi - Cumartesi: 09:00 - 18:00'}<br />
                                            Pazar: Kapalı
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="bg-white p-8 rounded-xl shadow-lg">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">
                                Mesaj Gönderin
                            </h2>
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                        Ad Soyad *
                                    </label>
                                    <Input
                                        id="name"
                                        name="name"
                                        type="text"
                                        required
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Adınızı girin"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                        E-posta *
                                    </label>
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="E-posta adresinizi girin"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                                        Telefon *
                                    </label>
                                    <Input
                                        id="phone"
                                        name="phone"
                                        type="tel"
                                        required
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="Telefon numaranızı girin"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                                        Mesajınız *
                                    </label>
                                    <Textarea
                                        id="message"
                                        name="message"
                                        required
                                        value={formData.message}
                                        onChange={handleChange}
                                        placeholder="Mesajınızı buraya yazın"
                                        rows={5}
                                    />
                                </div>

                                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg">
                                    Mesaj Gönder
                                </Button>
                            </form>
                        </div>
                    </div>

                    {/* Google Maps */}
                    <div className="mt-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                            Konumumuz
                        </h2>
                        <div className="rounded-xl overflow-hidden shadow-lg">
                            <iframe
                                src={contactInfo.mapEmbed}
                                width="100%"
                                height="450"
                                style={{ border: 0 }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title="Konumumuz"
                            ></iframe>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ContactPage;
